import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CheckCircle, FileText, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { ausgangAPI, mandantenAPI } from '../lib/api';
import AusgangFormModal from '../components/AusgangFormModal';

const STATUS_LABELS = {
  entwurf: 'Entwurf',
  gebucht: 'Gebucht',
  exportiert: 'Exportiert',
  storniert: 'Storniert',
};

const STATUS_FILTERS = ['', 'entwurf', 'gebucht', 'exportiert', 'storniert'];

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);
}

function fmtDate(s) {
  if (!s) return '—';
  return format(new Date(s), 'dd.MM.yyyy');
}

export default function AusgangList() {
  const { slug } = useParams();
  const fileInputRef = useRef(null);
  const [mandant, setMandant] = useState(null);
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=create, obj=edit
  const [prefill, setPrefill] = useState(null);     // AI-Extraktions-Ergebnis
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reload = useCallback(() => {
    setLoading(true);
    return ausgangAPI
      .list(slug, { status: statusF || undefined })
      .then((r) => setItems(r.data))
      .catch((err) =>
        toast.error(err.response?.data?.detail || 'Ausgangsrechnungen konnten nicht geladen werden')
      )
      .finally(() => setLoading(false));
  }, [slug, statusF]);

  useEffect(() => { reload(); }, [reload]);

  const onBuchen = async (a) => {
    if (!window.confirm(`Rechnung ${a.rechnungsnummer} buchen? Danach nicht mehr änderbar.`)) return;
    try {
      await ausgangAPI.buchen(slug, a.id);
      toast.success('Gebucht');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Buchen fehlgeschlagen');
    }
  };

  const onPickPdf = () => fileInputRef.current?.click();

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    const toastId = toast.loading('Lade PDF hoch und extrahiere mit KI…');
    try {
      const r = await ausgangAPI.uploadExtract(slug, file);
      toast.success('Extraktion fertig — bitte prüfen', { id: toastId });
      setPrefill(r.data);
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload/Extraktion fehlgeschlagen', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Entwurf "${a.rechnungsnummer}" löschen?`)) return;
    try {
      await ausgangAPI.remove(slug, a.id);
      toast.success('Gelöscht');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Ausgangsrechnungen</h1>
          {mandant && (
            <div className="muted">{mandant.name} · {mandant.skr_variant}</div>
          )}
        </div>
        <div className="header-meta" style={{ gap: 8, display: 'flex' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif,.webp"
            style={{ display: 'none' }}
            onChange={onFileChosen}
          />
          <button className="btn-secondary" onClick={onPickPdf} disabled={uploading}>
            <Upload size={14} /> {uploading ? 'Extrahiert…' : 'PDF hochladen'}
          </button>
          <button className="btn-primary" onClick={() => { setPrefill(null); setEditing(null); }}>
            <Plus size={14} /> Neue Rechnung
          </button>
        </div>
      </header>

      <div className="filters">
        <label>
          Status:
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{s ? STATUS_LABELS[s] : 'Alle'}</option>
            ))}
          </select>
        </label>
        <button className="btn-text" onClick={reload} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
      </div>

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : items.length === 0 ? (
        <div className="card empty">
          Keine Ausgangsrechnungen — neue über den Button rechts oben anlegen.
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Rechnungs-Nr</th>
              <th>Kunde</th>
              <th>Netto</th>
              <th>USt</th>
              <th>Brutto</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id}>
                <td>{fmtDate(a.beleg_datum)}</td>
                <td>
                  <b>{a.rechnungsnummer}</b>
                  {a.page_count > 0 && a.file_object_key !== undefined && (
                    <a
                      href={ausgangAPI.fileUrl(slug, a.id)}
                      target="_blank"
                      rel="noreferrer"
                      title="PDF öffnen"
                      style={{ marginLeft: 6 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FileText size={12} />
                    </a>
                  )}
                </td>
                <td>{a.kunde_name || `Kunde #${a.kunden_id}`}</td>
                <td className="num">{fmtMoney(a.betrag_netto)}</td>
                <td className="num">{fmtMoney(a.ust_betrag)} <span className="muted small">({Number(a.ust_satz)}%)</span></td>
                <td className="num"><b>{fmtMoney(a.betrag_brutto)}</b></td>
                <td>
                  <span className={`badge badge-${a.status}`}>
                    {STATUS_LABELS[a.status] || a.status}
                  </span>
                </td>
                <td className="row-actions">
                  {a.status === 'entwurf' && (
                    <>
                      <button className="btn-text" onClick={() => setEditing(a)} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="btn-text" onClick={() => onBuchen(a)} title="Buchen">
                        <CheckCircle size={14} />
                      </button>
                      <button className="btn-text" onClick={() => onDelete(a)} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AusgangFormModal
        open={editing !== undefined}
        onClose={() => { setEditing(undefined); setPrefill(null); }}
        slug={slug}
        ausgang={editing || undefined}
        prefill={editing === null ? prefill : null}
        onSaved={reload}
      />
    </div>
  );
}
